import { useState, useEffect } from 'react';
import { XCircle, AlertTriangle, ExternalLink, Loader2, CheckCheck, Trash, Info, BadgeCheck, Mail } from 'lucide-react';

import DnsTable from './domains/DnsTable.tsx';

import RemoveDialog from './domains/RemoveDialog.tsx';
import InfoDialog from './domains/InfoDialog.tsx';
import DropDownMenu from './domains/DropDownMenu.tsx';
import ActiveDomainCard from './domains/ActiveDomainCard.tsx';
import { EmailsCard } from './domains/EmailsCard.tsx';
import Instructions from './domains/Instructions.tsx';
import { useDomainStatusCheck } from '../../hooks/useDomainStatus.ts';
import { useUserDomain } from '../../hooks/useUserDomain.ts';
import { Button } from '../ui/button.tsx';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card.tsx';
import AddDomain from './domains/AddDomain.tsx';

function DomainManager() {
  const [showRemove, setShowRemove] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  
  const { data: userDomain } = useUserDomain();
  const { mutate: checkDomainStatus, isPending: statusLoading } = useDomainStatusCheck();
  
  useEffect(() => {
    setStatusError(null)
  }, [userDomain, checkDomainStatus]);
  
  const statusMessages = {
    pending_verification: 'Invalid Configuration',
    misconfigured : 'Misconfigured',
    removed: 'Removed', 
    active: 'Active',
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {userDomain?.status === 'active' ? <BadgeCheck className="size-5 mr-2 text-green-400" /> : <Info className="size-5 mr-2 text-yellow-400" />}
            Connect Your Domain
          </CardTitle>
          <CardDescription>
            Add your own domain to create a fully branded experience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6  pb-10">
          {!userDomain && <AddDomain />}

          {userDomain && userDomain.status === 'active' && (
              <ActiveDomainCard 
                userDomain={userDomain} 
                setShowInfo={setShowInfo} 
                setShowRemove={setShowRemove} 
                statusMessages={statusMessages} 
              />
            )}

            {/* Email Configuration Warning - Show only if domain is not active OR email is not verified */}
            {(!userDomain || userDomain.status !== 'active' || userDomain.email_status !== 'verified') && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/30">
                <div className="flex items-start space-x-3">
                  <Mail className="size-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                      Important: Email Configuration Required
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      After connecting your domain, <strong>you must configure email sending</strong>. 
                      Without this setup, your clients will not receive automatic notifications about appointments, 
                      confirmations, and other important events.
                    </p>
                  </div>
                </div>
              </div>
            )}
            

            {userDomain && userDomain.status !== 'active' && (
              <>
                <div className="flex  justify-between rounded-lg">
                  <div className="flex items-center gap-4">
                        <a className="hover:underline flex items-center gap-2 font-bold text-2xl" href={`https://${userDomain.domain}`} target="_blank" rel="noopener noreferrer">
                        {userDomain.domain} 
                        <ExternalLink className="size-5" />
                        </a>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        userDomain.status === 'pending_verification' 
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {userDomain.status === 'pending_verification' && <AlertTriangle className="size-4 mr-1" />}
                        {(userDomain.status === 'misconfigured' || userDomain.status === 'removed') && <XCircle className="size-4 mr-1" />}
                        {statusMessages[userDomain.status]}
                      </div>
                  </div>
                  <DropDownMenu
                    dropdownOpen={dropdownOpen}
                    setDropdownOpen={setDropdownOpen}
                    isActive={false}
                    setShowInfo={setShowInfo}
                    setShowRemove={setShowRemove}
                  />
                </div>
                <div className="space-y-6">
                  <Instructions title="Follow these steps to connect your domain:" />
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">DNS Records</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Copy these records to your DNS provider</p>
                    </div>
                    <DnsTable items={userDomain.instructions} />
                  </div>
                </div>
                <div className="flex justify-between space-x-2 mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30">
                  <div>
                    <div className="">
                      <p className="text-red-600 dark:text-red-400 ">
                        Delete <span className="font-semibold ">{userDomain.domain}</span> domain?
                      </p>
                      <p className="text-red-400 text-xs">
                      Once you delete your domain, there is no going back. Please be certain.
                      </p>

                    </div>
                  </div>
                  <Button 
                    variant="destructive" size="sm" 
                    onClick={() => setShowRemove(true)}
                    disabled={statusLoading}
                  >
                    <Trash className="size-4" /> 
                    Delete Domain
                  </Button>
             </div>
              </>
            )}


            {statusError && (
              <div className="flex items-center space-x-2 mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30">
                <XCircle className="h-4 w-4 text-red-500" />
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">{statusError}</p>
              </div>
            )}

            {userDomain && userDomain.status !== 'active' && (
              <div className="flex pt-4 w-full">
                <Button 
                  onClick={() => checkDomainStatus(userDomain, { onSuccess: () => setStatusError(null), onError: (error) => setStatusError(error.message) })} 
                  disabled={statusLoading}
                  size="lg"
                  variant="outline"
                >
                  {
                    statusLoading 
                      ? (<><Loader2 className="mr-2 size-5 animate-spin" /> Checking...</>) 
                      : (<><CheckCheck className="mr-2 size-5" />Check Status</>)
                  }
                </Button>
              </div>
            )}
          </div>
          {userDomain && userDomain.status === 'active' && <EmailsCard />}
        </CardContent>
      </Card>
      <RemoveDialog 
        showRemove={showRemove} 
        setShowRemove={setShowRemove} 
        userDomain={userDomain!} 
      />
      <InfoDialog 
        showInfo={showInfo} 
        setShowInfo={setShowInfo} 
        instructions={userDomain?.instructions || []} 
      />
    </div>
  );
}

export default DomainManager;